import React, { useEffect, useState } from 'react';
import api from '../api';
import { CO_LABEL } from '../utils/contractLabel';

/**
 * New customer, or (with `customer`) edit one. Customers are never deleted. The Customer No (Business
 * Central ID) of a customer in use (with POs or trials) can only be changed by an admin, who is warned
 * that the change applies everywhere.
 */
export default function CustomerModal({ open, onClose, onCreated, onUpdated, customer = null, isAdmin = false }) {
  const [customerid, setCustomerid] = useState('');
  const [name, setName] = useState('');
  const [closed, setClosed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCustomerid(customer ? String(customer.customerid ?? '') : '');
    setName(customer?.name ?? '');
    setClosed(Boolean(customer?.closed));
    setError(null);
  }, [open, customer]);

  const numberLocked = Boolean(customer?.in_use) && !isAdmin;
  const numberChanged = customer && String(customer.customerid) !== String(customerid);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!customerid || !name.trim()) {
      setError('Customer No and Name are required.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await api.post('/masterdata/customers/store', {
        ...(customer ? { id: customer.id } : {}),
        customerid,
        name: name.trim(),
        closed: closed ? 1 : 0,
      });
      if (customer) onUpdated?.(); else onCreated?.();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save customer');
    } finally {
      setLoading(false);
    }
  }

  // Dismiss modal with Esc
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={e => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      tabIndex={-1}
    >
      <div role="dialog" aria-label={customer ? 'Edit customer' : 'New customer'} className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-6 w-full max-w-md relative" onClick={e => e.stopPropagation()}>
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <h2 className="text-xl font-bold mb-4">{customer ? 'Edit Customer' : 'New Customer'}</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="customer-no" className="block text-sm font-medium mb-1">Customer No (Business Central ID) *</label>
            <input
              id="customer-no"
              type="text"
              inputMode="numeric"
              maxLength={10}
              className="w-full border rounded px-2 py-1 dark:bg-gray-800 dark:border-gray-700 read-only:bg-gray-100 read-only:text-gray-600 dark:read-only:bg-gray-900"
              value={customerid}
              onChange={e => setCustomerid(e.target.value.replace(/\D/g, ''))}
              readOnly={numberLocked}
              required
            />
            {numberLocked && (
              <p className="mt-1 text-xs text-gray-500">This customer has {CO_LABEL}s or trials, so only an admin can change its number.</p>
            )}
            {customer?.in_use && isAdmin && numberChanged && (
              <p role="status" className="mt-1 text-xs text-amber-700 dark:text-amber-300">This customer is in use: the new number applies everywhere it is shown.</p>
            )}
          </div>
          <div>
            <label htmlFor="customer-name" className="block text-sm font-medium mb-1">Name *</label>
            <input
              id="customer-name"
              type="text"
              maxLength={255}
              className="w-full border rounded px-2 py-1 dark:bg-gray-800 dark:border-gray-700"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="closed"
              checked={closed}
              onChange={e => setClosed(e.target.checked)}
            />
            <label htmlFor="closed" className="text-sm">Closed</label>
          </div>
          {error && <div role="alert" className="text-red-500 text-sm">{error}</div>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
              onClick={onClose}
              disabled={loading}
            >Cancel</button>
            <button
              type="submit"
              className="px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              disabled={loading}
            >{loading ? 'Saving...' : customer ? 'Save' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
